import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import Logo from '../common/Logo';
import SafeIcon from '../../common/SafeIcon';

const ADMIN_MENU = [
  {
    title: 'Main',
    items: [
      { name: 'Dashboard', icon: 'Home', path: '/dashboard' },
    ]
  },
  {
    title: 'HR Core',
    items: [
      { name: 'Employee Database', icon: 'Users', path: '/employees' },
      { name: 'Leave & Absence', icon: 'Calendar', path: '/leave' },
      { name: 'Performance', icon: 'Target', path: '/performance' },
      { name: 'Benefits', icon: 'Gift', path: '/benefits' },
      { name: 'Training (L&D)', icon: 'BookOpen', path: '/training' },
    ]
  },
  {
    title: 'Recruitment (ATS)',
    items: [
      { name: 'Positions', icon: 'Briefcase', path: '/positions' },
      { name: 'Applications', icon: 'FileText', path: '/applications' },
      { name: 'Marine Crew Pool', icon: 'Anchor', path: '/marine-crew' },
      { name: 'Review Workflow', icon: 'GitPullRequest', path: '/workflow' },
      { name: 'Talent Pool', icon: 'Database', path: '/candidates' },
    ]
  },
  {
    title: 'Immigration & Logistics',
    items: [
      { name: 'Immigration Tracker', icon: 'Globe', path: '/immigration' },
    ]
  },
  {
    title: 'System',
    items: [
      { name: 'Reports & Analytics', icon: 'BarChart2', path: '/analytics' },
      { name: 'Communication', icon: 'Mail', path: '/communication' },
      { name: 'Settings', icon: 'Settings', path: '/settings' },
    ]
  }
];

const CANDIDATE_MENU = [
  {
    title: 'Discovery',
    items: [
      { name: 'Browse Jobs', icon: 'Briefcase', path: '/jobs' },
      { name: 'My Applications', icon: 'FileText', path: '/my-applications' },
    ]
  },
  {
    title: 'Profile',
    items: [
      { name: 'My Profile', icon: 'User', path: '/profile' },
      { name: 'Certificates', icon: 'Award', path: '/my-certificates' },
      { name: 'Notifications', icon: 'Bell', path: '/notifications' },
    ]
  }
];

const EMPLOYEE_MENU = [
  {
    title: 'Self Service',
    items: [
      { name: 'My Dashboard', icon: 'Home', path: '/employee-dashboard' },
      { name: 'My Profile', icon: 'User', path: '/profile' },
      { name: 'Leave Requests', icon: 'Calendar', path: '/leave' },
      { name: 'My Benefits', icon: 'Gift', path: '/benefits' },
      { name: 'Performance', icon: 'Target', path: '/performance' },
      { name: 'Training', icon: 'BookOpen', path: '/training' },
    ]
  },
  {
    title: 'Company',
    items: [
      { name: 'Job Tracker', icon: 'Clipboard', path: '/job-tracker' },
      { name: 'Internal Jobs', icon: 'InternalJobs', path: '/internal-jobs' },
    ]
  }
];

const Sidebar = ({ isOpen, setIsOpen, isCollapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const userRole = localStorage.getItem('userRole');
  const isCandidate = userRole === 'candidate';
  const isEmployee = userRole === 'employee';

  const menuGroups = isEmployee ? EMPLOYEE_MENU : (isCandidate ? CANDIDATE_MENU : ADMIN_MENU);

  const [expandedGroups, setExpandedGroups] = useState(menuGroups.map(g => g.title));

  const toggleGroup = (title) => {
    setExpandedGroups(prev => 
      prev.includes(title) ? prev.filter(t => t !== title) : [...prev, title]
    );
  };

  const handleLogout = () => {
    localStorage.removeItem('userRole');
    navigate('/');
  };

  return (
    <>
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-20 lg:hidden" 
          onClick={() => setIsOpen(false)} 
        />
      )}

      <aside 
        className={`fixed top-0 left-0 z-30 h-screen bg-[#2F3349] text-[#CFD3EC] transition-all duration-300 flex flex-col shadow-xl overflow-hidden
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${isCollapsed ? 'w-20' : 'w-64'}`}
      >
        <div className={`h-16 flex items-center border-b border-white/10 shrink-0 ${isCollapsed ? 'justify-center px-0' : 'px-6'}`}>
          <Logo className={isCollapsed ? "h-8 w-8" : "h-10 w-auto"} collapsed={isCollapsed} />
        </div>

        <div className="flex-1 overflow-y-auto py-6 px-3 space-y-6 scrollbar-hide">
          {menuGroups.map((group) => (
            <div key={group.title} className="space-y-1">
              {!isCollapsed && (
                <button 
                  onClick={() => toggleGroup(group.title)}
                  className="w-full flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400 mb-2 px-3 hover:text-white transition-colors"
                >
                  {group.title}
                  <SafeIcon name={expandedGroups.includes(group.title) ? "ChevronDown" : "ChevronRight"} size={12} />
                </button>
              )}
              
              {(!isCollapsed && expandedGroups.includes(group.title) || isCollapsed) && group.items.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.path}
                  className={({ isActive }) => 
                    `flex items-center rounded-lg transition-all duration-200 group
                    ${isCollapsed ? 'justify-center py-3' : 'px-3 py-2.5 gap-3'}
                    ${isActive 
                      ? 'bg-gradient-to-r from-[#11469A] to-[#00AEEF] text-white shadow-md' 
                      : 'hover:bg-white/5 hover:text-white'}`
                  }
                >
                  <SafeIcon name={item.icon || 'Circle'} size={20} />
                  {!isCollapsed && <span className="font-medium whitespace-nowrap text-sm">{item.name}</span>}
                </NavLink>
              ))}
            </div>
          ))}
        </div>

        <div className="p-4 border-t border-white/10 shrink-0 bg-[#2F3349]">
          <button 
            onClick={handleLogout}
            className={`flex items-center text-[#00AEEF] hover:text-white hover:bg-white/5 transition-all rounded-lg w-full ${isCollapsed ? 'justify-center py-3' : 'px-3 py-2.5 gap-3'}`}
          >
            <SafeIcon name="LogOut" size={20} />
            {!isCollapsed && <span className="font-medium text-sm">Logout</span>}
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;